import { useState } from "react";
import Reveal from "@/components/Reveal";
import { Calculator, Info } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function ProfitCalculator() {
  const [revenue, setRevenue] = useState(6000);
  const [expenses, setExpenses] = useState(1000);

  const profit = Math.max(0, (Number(revenue) || 0) - (Number(expenses) || 0));
  const landlord = profit * 0.8;
  const shonta = profit * 0.2;

  return (
    <section id="calculator" className="py-20 sm:py-28" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF5A5F]">Transparent by Design</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#222]">
            How the 80/20 Profit Split Works
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#5a5a5a] max-w-2xl mx-auto">
            Enter your estimated numbers below and see exactly how monthly profits would be shared.
          </p>
        </Reveal>

        <Reveal className="mt-12">
          <div className="max-w-4xl mx-auto bg-white border border-[#EBEBEB] rounded-[2rem] shadow-[0_12px_32px_rgba(0,0,0,0.06)] overflow-hidden grid md:grid-cols-2">
            <div className="p-8 sm:p-10">
              <div className="flex items-center gap-2 text-sm font-bold tracking-wide uppercase text-[#717171]">
                <Calculator className="w-4 h-4 text-[#FF5A5F]" />
                Profit Calculator
              </div>
              <div className="mt-6 space-y-6">
                <div>
                  <label htmlFor="calc-revenue" className="text-sm font-bold text-[#222]">
                    Estimated Monthly Rental Revenue
                  </label>
                  <div className="mt-2 flex items-center bg-[#F7F7F9] border border-[#EBEBEB] rounded-xl px-4 focus-within:ring-2 focus-within:ring-[#FF5A5F]">
                    <span className="text-[#717171] font-bold">$</span>
                    <input
                      id="calc-revenue"
                      data-testid="calc-revenue-input"
                      type="number"
                      min="0"
                      value={revenue}
                      onChange={(e) => setRevenue(e.target.value)}
                      className="w-full bg-transparent px-2 py-3.5 text-lg font-bold text-[#222] outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="calc-expenses" className="text-sm font-bold text-[#222]">
                    Agreed Operating Expenses
                  </label>
                  <div className="mt-2 flex items-center bg-[#F7F7F9] border border-[#EBEBEB] rounded-xl px-4 focus-within:ring-2 focus-within:ring-[#FF5A5F]">
                    <span className="text-[#717171] font-bold">$</span>
                    <input
                      id="calc-expenses"
                      data-testid="calc-expenses-input"
                      type="number"
                      min="0"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      className="w-full bg-transparent px-2 py-3.5 text-lg font-bold text-[#222] outline-none"
                    />
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-[#717171] flex gap-2" data-testid="calc-disclaimer">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  Illustrative example only — not a projected or guaranteed result. Actual revenue and
                  expenses vary by property, market, and season.
                </p>
              </div>
            </div>

            <div className="bg-[#222222] p-8 sm:p-10 text-white flex flex-col justify-center" data-testid="calc-breakdown">
              <div className="space-y-4 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-white/60 font-semibold">Monthly Rental Revenue</span>
                  <span className="font-bold" data-testid="calc-row-revenue">{fmt(Number(revenue) || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/60 font-semibold">Agreed Operating Expenses</span>
                  <span className="font-bold text-white/70" data-testid="calc-row-expenses">− {fmt(Number(expenses) || 0)}</span>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-white/10">
                  <span className="font-bold">Remaining Monthly Profit</span>
                  <span className="font-extrabold text-lg" data-testid="calc-row-profit">{fmt(profit)}</span>
                </div>
              </div>
              <div className="mt-8 space-y-4">
                <div className="bg-[#FF5A5F] rounded-2xl px-6 py-5 flex justify-between items-center">
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase opacity-80">You Receive — 80%</p>
                    <p className="text-xs opacity-70 mt-0.5">Paid to you, the property owner</p>
                  </div>
                  <p className="font-heading text-3xl font-extrabold" data-testid="calc-row-landlord">{fmt(landlord)}</p>
                </div>
                <div className="bg-white/10 rounded-2xl px-6 py-5 flex justify-between items-center">
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-white/60">Shonta Solutions — 20%</p>
                    <p className="text-xs text-white/45 mt-0.5">For managing the rental</p>
                  </div>
                  <p className="font-heading text-2xl font-extrabold text-white/80" data-testid="calc-row-shonta">{fmt(shonta)}</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
